import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { useGetInParkingCard } from 'src/features/cards/api/useGetInParkingCard';
import { useListCardsInParking } from 'src/features/cards/api/useListCardsInParking';
import CardModalFormButtons from 'src/features/cards/components/CardModalFormButtons';
import useCalculateStayTime from 'src/features/cards/hooks/useCalculateStayTime';
import { badgeCardTypeOptions } from 'src/features/cards/utils/constants';
import { cardMessages } from 'src/features/cards/utils/messages';
import { EventType } from 'src/features/events/types/event';
import { badgeStatusOptions, eventMessages } from 'src/features/events/utils';
import { useParking } from 'src/providers/ParkingProvider';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import ImageModal from 'src/registry/ui/ImageModal';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { cn } from 'src/utils';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

import type { Event, SidePanelAttributesType } from 'src/features/events/types/event';
import type { BadgeOption } from 'src/types';
import type { InParkingCard } from 'src/features/cards/types';

type EventDetailsProps = {
    event?: Event;
};

export function EventDetails(props: EventDetailsProps) {
    const { event } = props;

    const { parkingId, parkingCurrency } = useParking();
    const { formatMessage, formatNumber } = useIntl();
    const { calculateStayTime } = useCalculateStayTime();
    const { data: cardInParkingData } = useListCardsInParking({
        parkingId,
        card_number: event?.card_number,
        config: { enabled: !!event?.card_number },
    });

    const inParkingDataForEvent: InParkingCard | undefined = cardInParkingData?.results
        ? cardInParkingData?.results[0]
        : undefined;

    const {
        data: cardData,
        isLoading,
        isFetching,
        refetch,
    } = useGetInParkingCard({
        parkingId,
        id: inParkingDataForEvent?.id,
        config: { enabled: !!inParkingDataForEvent?.id || !event?.card_number },
    });

    const [stayTime, setStayTime] = React.useState('');

    const isVehicleInParking = !!cardData?.in_parking;

    React.useEffect(() => {
        const stayString = calculateStayTime(cardData ? cardData : inParkingDataForEvent);
        setStayTime(stayString);

        const interval = setInterval(() => {
            const stayString = calculateStayTime(cardData ? cardData : inParkingDataForEvent);
            setStayTime(stayString);
        }, 60 * 1000); // Update every minute

        return () => clearInterval(interval);
    }, [calculateStayTime, cardInParkingData, cardData]);

    const statusTypeBadge: BadgeOption | undefined = event && badgeStatusOptions[event?.event_type];

    const getParkingCardAttributesByEvent = (eventType: EventType | undefined): SidePanelAttributesType[] => {
        if (eventType === undefined) return [{ title: '', value: '', control: undefined }];

        const exitEvents = ['exit', 'exit-attempt', 'exit-success', 'exit-failed'];
        const exitOrEntryPoint = exitEvents.includes(eventType) ? cardMessages.exitPoint : cardMessages.entryPoint;

        const service = [
            {
                title: formatMessage(eventMessages.timestamp),
                value: getFormattedDateTimeForDetailPanel(event?.timestamp),
                control: event,
            },
            {
                title: formatMessage(eventMessages.event),
                value: '',
                control: event,
                isBadge: true,
                badgeValue: statusTypeBadge,
            },
            { title: formatMessage(eventMessages.message), value: event?.message, control: event },
        ];
        const entryExitAttributes = [
            {
                title: formatMessage(eventMessages.timestamp),
                value: getFormattedDateTimeForDetailPanel(event?.timestamp),
                control: event,
            },
            {
                title: formatMessage(eventMessages.event),
                value: '',
                control: event,
                isBadge: true,
                badgeValue: statusTypeBadge,
            },
            { title: formatMessage(exitOrEntryPoint), value: event?.point_name, control: event },
            { title: formatMessage(eventMessages.message), value: event?.message, control: event },
        ];

        const eventTypeAttributes = {
            service,
            entry: entryExitAttributes,
            exit: entryExitAttributes,
            'entry-attempt': entryExitAttributes,
            'entry-success': entryExitAttributes,
            'entry-failed': entryExitAttributes,
            'exit-attempt': entryExitAttributes,
            'exit-success': entryExitAttributes,
            'exit-failed': entryExitAttributes,
        };

        return eventTypeAttributes[eventType];
    };

    return (
        <>
            {event ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{event.card_number}</DialogTitle>
                        <Badge theme={badgeCardTypeOptions[event.card_type].theme}>
                            {formatMessage(badgeCardTypeOptions[event.card_type].label)}
                        </Badge>
                    </div>
                    <DialogDescription>{event.point_name || '-'}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}
            <DialogSeparator />
            <DialogBody>
                <ImageModal
                    image={`/parkings/images/${parkingId}/${event?.photo_path}`}
                    alt={event?.card_number}
                    title={event?.card_number}
                    disableClick={!event?.photo_path}
                />

                {event?.event_type &&
                    event.event_type !== EventType.SERVICE &&
                    event.event_type !== EventType.ENTRY_FAILED &&
                    event.event_type !== EventType.EXIT_SUCCESS && (
                        <CardModalFormButtons
                            data={cardData}
                            refetch={refetch}
                            cardInParkingData={inParkingDataForEvent}
                        />
                    )}

                <div className="mt-4 grid gap-2">
                    {getParkingCardAttributesByEvent(event?.event_type).map((attribute) => {
                        const badgeAttribute = attribute.badgeValue;
                        return !attribute.isBadge ? (
                            <SidePanelBodyAttribute key={attribute.title}>
                                <SidePanelBodyAttributeTitle>{attribute.title}</SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={attribute.control}>
                                    {attribute.value}
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>
                        ) : (
                            <SidePanelBodyAttribute key={attribute.title}>
                                <SidePanelBodyAttributeTitle>{attribute.title}</SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={attribute.control}>
                                    <Badge
                                        theme={badgeAttribute?.theme || 'gray'}
                                        className="gap-1.5 whitespace-nowrap"
                                    >
                                        {badgeAttribute?.icon && (
                                            <FontAwesomeIcon
                                                icon={badgeAttribute.icon}
                                                className={cn('z-0', !badgeAttribute.theme && 'opacity-50')}
                                            />
                                        )}
                                        {badgeAttribute?.label ? formatMessage(badgeAttribute.label) : '-'}
                                    </Badge>
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>
                        );
                    })}
                </div>
                {event?.event_type && event.event_type !== EventType.SERVICE && <DialogSeparator />}

                {isLoading && !isFetching ? null : isVehicleInParking ? (
                    <div className="mt-4 grid gap-2">
                        <SidePanelBodyAttribute>
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(cardMessages.cardNumber)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={cardData?.card_number || inParkingDataForEvent?.plate_number}
                                fallbackMessage={formatMessage(generalMessages.noValue)}
                            >
                                {cardData?.card_number ? cardData?.card_number : inParkingDataForEvent?.card_number}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute>
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(cardMessages.parkingZone)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={inParkingDataForEvent?.parking_name}
                                fallbackMessage={formatMessage(generalMessages.noValue)}
                            >
                                {inParkingDataForEvent?.parking_name}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute>
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(cardMessages.currentStay)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={stayTime}
                                fallbackMessage={formatMessage(generalMessages.noValue)}
                            >
                                {stayTime}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute>
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(cardMessages.chargePlan)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={inParkingDataForEvent?.charge_plan_name}
                                fallbackMessage={formatMessage(generalMessages.noValue)}
                            >
                                {inParkingDataForEvent?.charge_plan_name}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute>
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(cardMessages.dueAmount)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={cardData?.due_amount}
                                fallbackMessage={formatMessage(generalMessages.noValue)}
                            >
                                {cardData &&
                                    formatNumber(cardData.due_amount / 100, {
                                        style: 'currency',
                                        currency: parkingCurrency,
                                    })}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute>
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(cardMessages.taxAmount)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={cardData?.tax_amount}
                                fallbackMessage={formatMessage(generalMessages.noValue)}
                            >
                                {cardData &&
                                    formatNumber(cardData.tax_amount / 100, {
                                        style: 'currency',
                                        currency: parkingCurrency,
                                    })}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute>
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(cardMessages.totalDue)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={cardData?.due_amount_with_tax}
                                fallbackMessage={formatMessage(generalMessages.noValue)}
                            >
                                {cardData &&
                                    formatNumber(cardData.due_amount_with_tax / 100, {
                                        style: 'currency',
                                        currency: parkingCurrency,
                                    })}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>
                    </div>
                ) : (
                    event?.event_type &&
                    event.event_type !== EventType.SERVICE && (
                        <div>{formatMessage(cardMessages.vehicleIsNotInParking)}</div>
                    )
                )}
            </DialogBody>
            <DialogSeparator />
            <DialogFooter className="flex flex-col gap-2 sm:flex-col">
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
